import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { ApiError, userAuth } from '../api.js';

export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handle(e: FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await userAuth.forgotPassword(email.trim());
      setDone(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Network error.');
    } finally {
      setSubmitting(false);
    }
  }

  if (done) {
    return (
      <div className="max-w-sm mx-auto py-16 text-center">
        <h1 className="font-serif text-3xl mb-2">Check your inbox</h1>
        <p className="text-muted text-sm mb-6">
          If that email is registered, a reset link is on its way. The link is good for 1 hour.
        </p>
        <Link to="/login" className="text-accent underline-offset-2 hover:underline text-sm">Back to sign in</Link>
      </div>
    );
  }

  return (
    <div className="max-w-sm mx-auto py-16">
      <h1 className="font-serif text-3xl mb-2">Reset your password</h1>
      <p className="text-muted text-sm mb-8">
        Enter your email and we will send you a link to set a new password.
      </p>
      <form onSubmit={handle} className="space-y-4">
        <div>
          <label className="block text-sm text-muted mb-1.5" htmlFor="email">Email</label>
          <input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoFocus
            autoComplete="email"
            className="w-full px-3 py-2 bg-white border border-soft rounded-md focus:outline-none focus:ring-2 focus:ring-accent/40 focus:border-accent/60 text-sm"
            required
          />
        </div>
        {error && (
          <div className="text-sm text-accent bg-accent/5 border border-accent/20 rounded-md px-3 py-2">{error}</div>
        )}
        <button
          type="submit"
          disabled={submitting || !email}
          className="w-full bg-ink text-parchment py-2 rounded-md hover:bg-ink/90 disabled:opacity-40 disabled:cursor-not-allowed transition-colors text-sm tracking-wide"
        >
          {submitting ? 'Sending…' : 'Send reset link'}
        </button>
        <p className="text-center text-sm text-muted">
          <Link to="/login" className="text-accent underline-offset-2 hover:underline">Back to sign in</Link>
        </p>
      </form>
    </div>
  );
}
